import { useCallback, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  FileCode2,
  Loader2,
  Play,
  XCircle,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  CANONICAL_ORIGIN,
  ROUTE_CONTRACTS,
  extractJsonLd,
  summarizeAudit,
  typeOf,
  validateNodes,
  type RouteAuditResult,
} from "@/lib/structuredDataAudit";

export interface SitemapSubmission {
  path: string;
  lastSubmitted: string | null;
  lastDownloaded: string | null;
  isPending: boolean;
  errors: number;
  warnings: number;
}

interface Props {
  /** Sitemap submissions as reported by Search Console (optional). */
  sitemaps?: SitemapSubmission[];
}

const formatDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const AUDIT_TIMEOUT_MS = 8000;

/** Load a route in a hidden same-origin iframe and read its rendered JSON-LD. */
async function auditRoute(
  path: string,
  container: HTMLElement,
  contract = ROUTE_CONTRACTS.find((r) => r.path === path)!,
): Promise<RouteAuditResult> {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  iframe.title = `structured-data audit ${path}`;
  iframe.style.cssText = "position:absolute;width:1024px;height:768px;left:-10000px;top:0;border:0;";
  container.appendChild(iframe);

  try {
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => reject(new Error("timed out loading route")), AUDIT_TIMEOUT_MS);
      iframe.addEventListener("load", () => {
        window.clearTimeout(timer);
        resolve();
      });
      iframe.addEventListener("error", () => {
        window.clearTimeout(timer);
        reject(new Error("failed to load route"));
      });
      iframe.src = path;
    });

    const doc = iframe.contentDocument;
    if (!doc) throw new Error("route document unavailable");

    // JSON-LD is injected client-side after hydration — poll briefly for it.
    const deadline = Date.now() + AUDIT_TIMEOUT_MS;
    let extracted = extractJsonLd(doc);
    while (extracted.blockCount === 0 && Date.now() < deadline) {
      await new Promise((r) => window.setTimeout(r, 200));
      extracted = extractJsonLd(doc);
    }

    const problems = [...extracted.errors, ...validateNodes(extracted.nodes, contract)];
    const nodeTypes = Array.from(new Set(extracted.nodes.flatMap((n) => typeOf(n)))).sort();

    return {
      path,
      status: problems.length === 0 ? "healthy" : "problems",
      problems,
      nodeTypes,
      blockCount: extracted.blockCount,
    };
  } catch (error) {
    return {
      path,
      status: "error",
      problems: [error instanceof Error ? error.message : "Unknown error"],
      nodeTypes: [],
      blockCount: 0,
    };
  } finally {
    iframe.remove();
  }
}

async function fetchSitemapPaths(): Promise<string[]> {
  const res = await fetch("/sitemap.xml", { cache: "no-store" });
  if (!res.ok) throw new Error(`sitemap.xml returned ${res.status}`);
  const xml = await res.text();
  const locs = Array.from(xml.matchAll(/<loc>\s*([^<\s]+)\s*<\/loc>/g)).map((m) => m[1]);
  return locs.map((loc) => {
    try {
      return new URL(loc).pathname.replace(/(.)\/$/, "$1");
    } catch {
      return loc;
    }
  });
}

function StatusBadge({ status }: { status: RouteAuditResult["status"] }) {
  if (status === "healthy") {
    return (
      <Badge className="gap-1 bg-primary/15 text-primary hover:bg-primary/20">
        <CheckCircle2 className="h-3 w-3" /> Valid
      </Badge>
    );
  }
  if (status === "problems") {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3 w-3" /> Issues
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      <AlertTriangle className="h-3 w-3" /> Not checked
    </Badge>
  );
}

export function StructuredDataHealthCard({ sitemaps }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState<RouteAuditResult[]>([]);
  const [sitemapPaths, setSitemapPaths] = useState<string[] | null>(null);
  const [sitemapError, setSitemapError] = useState<string | null>(null);
  const [lastRun, setLastRun] = useState<Date | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const summary = useMemo(() => summarizeAudit(results), [results]);

  const missingFromSitemap = useMemo(() => {
    if (!sitemapPaths) return [];
    const set = new Set(sitemapPaths);
    return ROUTE_CONTRACTS.map((r) => r.path).filter((p) => !set.has(p === "/" ? "/" : p));
  }, [sitemapPaths]);

  const runAudit = useCallback(async () => {
    const container = containerRef.current;
    if (!container || running) return;
    setRunning(true);
    setProgress(0);
    setResults([]);
    setSitemapError(null);

    try {
      const sitemapPromise = fetchSitemapPaths()
        .then((paths) => setSitemapPaths(paths))
        .catch((e: unknown) => setSitemapError(e instanceof Error ? e.message : "Failed to read sitemap.xml"));

      const collected: RouteAuditResult[] = [];
      for (let i = 0; i < ROUTE_CONTRACTS.length; i++) {
        const result = await auditRoute(ROUTE_CONTRACTS[i].path, container);
        collected.push(result);
        setResults([...collected]);
        setProgress(Math.round(((i + 1) / ROUTE_CONTRACTS.length) * 100));
      }

      await sitemapPromise;
      setLastRun(new Date());
    } finally {
      setRunning(false);
    }
  }, [running]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-2 text-lg">
          <span className="flex items-center gap-2">
            <FileCode2 className="h-4 w-4 text-primary" /> Structured data & sitemap health
          </span>
          <Button size="sm" variant="outline" onClick={runAudit} disabled={running}>
            {running ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Auditing…
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" /> Run audit
              </>
            )}
          </Button>
        </CardTitle>
        <CardDescription>
          Renders every public route in this build and validates its JSON-LD against the same rules the
          CI audit enforces (WebPage/Article/FAQ shape, breadcrumb order, canonical <code>@id</code>s, no
          duplicate or dangling entities), then checks sitemap coverage and submission state.
          {lastRun ? ` Last run ${lastRun.toLocaleTimeString()}.` : ""}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-5">
        {running ? <Progress value={progress} className="h-2" /> : null}

        {results.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Routes checked", summary.total],
                ["Valid", summary.healthy],
                ["With issues", summary.problems + summary.errors],
                ["Total issues", summary.problemCount],
              ].map(([label, value]) => (
                <div key={label as string} className="rounded-lg border bg-muted/40 p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-xl font-semibold">{value as number}</p>
                </div>
              ))}
            </div>

            <ul className="divide-y rounded-lg border">
              {results.map((r) => {
                const open = Boolean(expanded[r.path]);
                return (
                  <li key={r.path}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/50"
                      onClick={() => setExpanded((prev) => ({ ...prev, [r.path]: !prev[r.path] }))}
                      aria-expanded={open}
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {open ? (
                          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate font-mono text-xs">{r.path}</span>
                      </span>
                      <span className="flex items-center gap-2">
                        {r.problems.length > 0 ? (
                          <span className="text-xs text-muted-foreground">
                            {r.problems.length} issue{r.problems.length === 1 ? "" : "s"}
                          </span>
                        ) : null}
                        <StatusBadge status={r.status} />
                      </span>
                    </button>
                    {open ? (
                      <div className="space-y-2 border-t bg-muted/30 px-3 py-3 text-xs">
                        <p className="text-muted-foreground">
                          {r.blockCount} JSON-LD block{r.blockCount === 1 ? "" : "s"}
                          {r.nodeTypes.length > 0 ? ` · ${r.nodeTypes.join(", ")}` : ""}
                        </p>
                        {r.problems.length === 0 ? (
                          <p className="text-primary">No issues found.</p>
                        ) : (
                          <ul className="list-disc space-y-1 pl-4">
                            {r.problems.map((p, i) => (
                              <li key={i} className="break-words">
                                {p}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        ) : !running ? (
          <p className="text-sm text-muted-foreground">
            Run the audit to validate JSON-LD across all {ROUTE_CONTRACTS.length} public routes.
          </p>
        ) : null}

        <div className="space-y-3 border-t pt-4">
          <p className="text-sm font-medium">Sitemap</p>

          {sitemapError ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>sitemap.xml unavailable</AlertTitle>
              <AlertDescription>{sitemapError}</AlertDescription>
            </Alert>
          ) : sitemapPaths ? (
            <div className="space-y-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{sitemapPaths.length} URLs listed</Badge>
                {missingFromSitemap.length === 0 ? (
                  <Badge className="gap-1 bg-primary/15 text-primary hover:bg-primary/20">
                    <CheckCircle2 className="h-3 w-3" /> All audited routes covered
                  </Badge>
                ) : (
                  <Badge variant="destructive">{missingFromSitemap.length} routes missing</Badge>
                )}
              </div>
              {missingFromSitemap.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  Not in sitemap.xml: <span className="font-mono">{missingFromSitemap.join(", ")}</span>
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Run the audit to check sitemap coverage.</p>
          )}

          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              Search Console submission status
            </p>
            {!sitemaps ? (
              <p className="text-sm text-muted-foreground">
                Connect a Search Console property above to see submission status.
              </p>
            ) : sitemaps.length === 0 ? (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>No sitemap submitted</AlertTitle>
                <AlertDescription>
                  Submit <span className="font-mono">{CANONICAL_ORIGIN}/sitemap.xml</span> in Search
                  Console so Google discovers every route.
                </AlertDescription>
              </Alert>
            ) : (
              <ul className="space-y-2">
                {sitemaps.map((s) => (
                  <li key={s.path} className="rounded-lg border p-3 text-xs">
                    <p className="break-all font-mono">{s.path}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
                      <span>Submitted {formatDate(s.lastSubmitted)}</span>
                      <span>Downloaded {formatDate(s.lastDownloaded)}</span>
                      {s.errors > 0 ? <Badge variant="destructive">{s.errors} errors</Badge> : null}
                      {s.warnings > 0 ? <Badge variant="secondary">{s.warnings} warnings</Badge> : null}
                      {s.isPending ? <Badge variant="outline">Pending</Badge> : null}
                      {!s.isPending && s.errors === 0 ? (
                        <Badge className="gap-1 bg-primary/15 text-primary hover:bg-primary/20">
                          <CheckCircle2 className="h-3 w-3" /> Processed
                        </Badge>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div ref={containerRef} aria-hidden="true" className="sr-only" />
      </CardContent>
    </Card>
  );
}

export default StructuredDataHealthCard;
